<template>
  <Component
    :is="type === PrimaryActionType.COMPLETE ? ConfirmCompleteModal : Primitive"
    v-bind="
      type === PrimaryActionType.COMPLETE
        ? {
            title: $t('components.task_details.confirm_complete.title', 2),
            description: description,
            cancelLabel: $t(
              'components.task_details.confirm_complete.buttons.cancel',
              2
            ),
            confirmLabel: $t(
              'components.task_details.confirm_complete.buttons.confirm',
              2
            ),
          }
        : {
            asChild: true,
          }
    "
    @click="$emit('click', primaryAction?.id)"
  >
    <Button
      v-if="primaryAction"
      :label="$t(primaryAction?.label)"
      variant="primary"
      :type="primaryAction?.type"
      size="sm"
      :disabled="disabled"
      :aria-label="$t(primaryAction?.label)"
    >
      <template v-if="primaryAction?.icon" #start>
        <Icon :name="primaryAction?.icon" size="lg" />
      </template>
    </Button>
  </Component>
</template>

<script setup>
import { computed } from "vue";
import { Primitive } from "radix-vue";
import { Button } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { ConfirmCompleteModal } from "@/SharedComponents/Modals";
import { PrimaryActionType, primaryActions } from "@/SharedComponents/Tasks";
import { useI18n } from "vue-i18n";

defineEmits(["click"]);

const { t } = useI18n();

const props = defineProps({
  tasks: { type: Array, required: true },
  isArchivedView: { type: Boolean, required: false },
  canComplete: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const hasOpen = computed(() => props.tasks.some((task) => !task.completed));

const type = computed(() => {
  if (props.isArchivedView) {
    return PrimaryActionType.RESTORE;
  }

  if (!props.canComplete || !hasOpen.value) {
    return null;
  }

  return PrimaryActionType.COMPLETE;
});

const hasNoChecklistItems = computed(() => {
  return props.tasks.every((task) => !task.checklist_items);
});

const hasAllItemsChecked = computed(() => {
  return props.tasks.every(
    (task) => task.checked_checklist_items === task.checklist_items
  );
});

const description = computed(() => {
  if (hasNoChecklistItems.value || hasAllItemsChecked.value) {
    return t("components.task_details.confirm_complete.description", 2);
  }

  return t(
    "components.task_details.confirm_complete_unchecked_checklist_items.description",
    2
  );
});

const primaryAction = computed(() => {
  if (!type.value) {
    return null;
  }

  return primaryActions[type.value];
});
</script>
